import {
    type FC,
    type ReactElement,
    type ReactNode,
    type MutableRefObject,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { breakpoints } from 'dibs-sassy/exports/breakpoints.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

type BreakpointToNumColsMap = {
    desktop: number;
    tabletPortrait: number;
    tabletLandscape: number;
    mobile: number;
};

type CustomContainer = MutableRefObject<HTMLDivElement | null>;

type Props = {
    breakpointToNumColsMap?: BreakpointToNumColsMap;
    impressionTracking?: ({
        inViewport,
        rowIndex,
        columnElementGroups,
    }: {
        inViewport: boolean;
        rowIndex: number;
        columnElementGroups: ReactNode[];
    }) => void;
    numCols?: number;
    children: Array<ReactElement>;
    elementGap?: number;
    customContainer?: CustomContainer;
};

// Given an array of children elements and a number of columns, places children elements
// into their respective column group. Returns an array of arrays, where the outer array
// has a length equivalent to the number of columns. Children elements are placed in each
// inner array, left to right, one element at a time.
// ex. getColumnElementGroups({ children: [x, x, x, x, x], numColumns: 4 }) -> [[x, x], [x], [x], [x]]
const getColumnElementGroups = ({
    children,
    numCols,
}: {
    children: Array<ReactElement>;
    numCols: number;
}): ReactElement[][] => {
    const reduced = children.reduce((prev: Array<Array<ReactElement>>, curr, i) => {
        const group = i % numCols;

        if (!prev[group]) {
            prev[group] = [];
        }

        prev[group].push(curr);

        return prev;
    }, []);
    return reduced;
};

// Given a mapping of desired number of columns per breakpoint,
// returns the optimal number of columns to display (based on screen-width)
const calcNumCols = ({
    customContainer,
    breakpointToNumColsMap,
}: {
    customContainer?: CustomContainer;
    breakpointToNumColsMap: BreakpointToNumColsMap;
}): number => {
    const maxNumCols = breakpointToNumColsMap.desktop;
    let colNum = maxNumCols;

    // Custom container (narrower than global window) can be passed
    // to be used in column count calculation
    let customContainerWidth;
    if (customContainer) {
        const { width } = customContainer.current?.getBoundingClientRect() || {};
        customContainerWidth = width;
    }
    const { innerWidth } = global;
    const containerWidth = customContainerWidth ?? innerWidth;

    if (typeof containerWidth !== 'undefined') {
        const bps = Object.keys(breakpointToNumColsMap).sort((a: string, b: string) => {
            const prevMax = hasKey(breakpoints, a) ? breakpoints[a].max : 0;
            const currMax = hasKey(breakpoints, b) ? breakpoints[b].max : 0;
            return prevMax - currMax;
        });

        for (const bp of bps) {
            if (hasKey(breakpoints, bp) && containerWidth <= breakpoints[bp].max) {
                colNum = breakpointToNumColsMap[bp];
                break;
            }
        }
    }
    return colNum;
};

export const Masonry: FC<Props> = ({
    children,
    numCols = 4,
    impressionTracking,
    breakpointToNumColsMap = {
        desktop: 4,
        tabletPortrait: 3,
        tabletLandscape: 3,
        mobile: 1,
    },
    elementGap = 0,
    customContainer,
}) => {
    const [numColumns, setNumColumns] = useState(numCols);

    const updateNumColumns = useCallback(
        () => setNumColumns(calcNumCols({ customContainer, breakpointToNumColsMap })),
        [customContainer, breakpointToNumColsMap]
    );

    const getColumnStyle = useCallback(
        (columnIdx: number) => {
            return {
                // Round column widths to nearest hundredths in case of irrational numbers
                width: `${+(100 / numColumns).toFixed(2)}%`,
                rowGap: `${elementGap}px`,
                paddingLeft: `${elementGap / 2}px`,
                paddingRight: `${elementGap / 2}px`,
                ...(columnIdx === 0 && { paddingLeft: 0 }),
                ...(columnIdx === numColumns - 1 && { paddingRight: 0 }),
            };
        },
        [numColumns, elementGap]
    );

    useEffect(() => {
        // Set optimal number of columns when component mounts
        updateNumColumns();
        // Set optimal number of columns again if screen is re-sized
        global.addEventListener('resize', updateNumColumns);

        return () => global.removeEventListener('resize', updateNumColumns);
    }, [breakpointToNumColsMap, updateNumColumns]);

    if (!children || !children.length || !numColumns) {
        return null;
    }
    const columnElementGroups = getColumnElementGroups({ children, numCols: numColumns });

    return (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.justifyStart,
                dibsCss.flexRow,
                dibsCss.flexWrap
            )}
        >
            {columnElementGroups.filter(filterNulls).map((columnElements, columnIndex) => (
                <div
                    key={`masonry-row-${columnIndex}`}
                    style={getColumnStyle(columnIndex)}
                    className={classnames(dibsCss.flex, dibsCss.flexCol)}
                >
                    {columnElements.map((element, rowIndex) => {
                        // fire impressions for entire row when left-most column elements are in viewport
                        if (impressionTracking && columnIndex === 0) {
                            return (
                                <>
                                    <VisibilityTracker
                                        key={element?.key}
                                        onVisibilityChange={({ isVisible }) => {
                                            if (isVisible) {
                                                impressionTracking({
                                                    inViewport: true,
                                                    rowIndex,
                                                    columnElementGroups,
                                                });
                                            }
                                        }}
                                    />
                                    {element}
                                </>
                            );
                        } else {
                            return element;
                        }
                    })}
                </div>
            ))}
        </div>
    );
};
